import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/I18nContext';
import { Button } from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

// Local Asset Imports
import truffleImage from '../assets/images/truffle.jpg';
import tunaImage from '../assets/images/tuna.jpg';

const { FiArrowRight, FiShield, FiTruck, FiAward, FiSearch, FiShoppingBag, FiStar, FiCheck, FiLayers, FiQuote } = FiIcons;

const CategoryCard = ({ image, title, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    className="group relative aspect-[4/5] overflow-hidden rounded-2xl cursor-pointer"
  >
    <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
    <div className="absolute inset-0 gradient-overlay" />
    <div className="absolute bottom-0 left-0 p-6 w-full">
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <div className="h-0.5 w-0 bg-white transition-all duration-300 group-hover:w-12" />
    </div>
  </motion.div>
);

const ProductCard = ({ image, title, origin, price, delay }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay }}
      className="group flex flex-col gap-4"
    >
      <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-zinc-100 relative flex items-center justify-center">
        <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
            <SafeIcon icon={FiShoppingBag} className="w-4 h-4 text-zinc-900" />
          </button>
        </div>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#D4B483] mb-1">{origin}</p>
        <h4 className="text-lg font-bold text-zinc-900">{title}</h4>
        <p className="text-sm font-medium text-zinc-500">{price}</p>
      </div>
    </motion.div>
  );
};

const SupplierCard = ({ image, name, location, type, rating, productCount }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4 group">
      <div className="aspect-square rounded-2xl overflow-hidden grayscale group-hover:grayscale-0 transition-all duration-500 relative">
        <img src={image} alt={name} className="w-full h-full object-cover" />
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-[10px] font-black uppercase tracking-tighter flex items-center gap-1 shadow-sm">
          <SafeIcon icon={FiCheck} className="w-3 h-3 text-emerald-600" /> {t('common.verified')}
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-bold uppercase tracking-widest text-[#D4B483]">{type}</p>
          <div className="flex items-center gap-1 text-xs font-bold text-zinc-900">
            <SafeIcon icon={FiStar} className="w-3 h-3 fill-amber-400 text-amber-400" /> {rating}
          </div>
        </div>
        <h4 className="text-lg font-bold text-zinc-900 group-hover:text-[#1A2E22] transition-colors">{name}</h4>
        <div className="flex items-center justify-between mt-2">
          <p className="text-sm text-zinc-500">{location}</p>
          <p className="text-xs font-medium text-zinc-400">{productCount} {t('home.suppliers.products')}</p>
        </div>
      </div>
    </div>
  );
};

const Step = ({ number, title, description, icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    className="flex flex-col gap-6"
  >
    <div className="w-16 h-16 bg-[#1A2E22] text-[#D4B483] rounded-2xl flex items-center justify-center relative shadow-xl shadow-[#1A2E22]/20">
      <SafeIcon icon={icon} className="w-6 h-6" />
      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#D4B483] text-[#1A2E22] rounded-full flex items-center justify-center text-xs font-black ring-4 ring-[#FCFAF8]">
        {number}
      </div>
    </div>
    <div>
      <h3 className="text-xl font-bold text-zinc-900 mb-3">{title}</h3>
      <p className="text-zinc-500 leading-relaxed">{description}</p>
    </div>
  </motion.div>
);

export const Home = () => {
  const { t } = useTranslation();
  
  const categories = [
    { title: t('home.categories.produce'), image: 'https://images.unsplash.com/photo-1550989460-0adf9ea622e2?auto=format&fit=crop&q=80&w=800' },
    { title: t('home.categories.dairy'), image: 'https://images.unsplash.com/photo-1452195100486-9cc805987862?auto=format&fit=crop&q=80&w=800' },
    { title: t('home.categories.meat'), image: 'https://images.unsplash.com/photo-1558030006-450675393462?auto=format&fit=crop&q=80&w=800' },
    { title: t('home.categories.sea'), image: 'https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?auto=format&fit=crop&q=80&w=800' },
    { title: t('home.categories.bakery'), image: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&q=80&w=800' },
    { title: t('home.categories.wine'), image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?auto=format&fit=crop&q=80&w=800' },
  ];

  const products = [
    { 
      title: 'Wild Forest Truffles', 
      origin: 'Piedmont, IT', 
      price: '€600.00 / kg', 
      image: truffleImage 
    },
    { 
      title: 'Artisan Sourdough', 
      origin: 'Antwerp, BE', 
      price: '€4.50 / unit', 
      image: 'https://images.unsplash.com/photo-1549931319-a545dcf3bc73?auto=format&fit=crop&q=80&w=800' 
    },
    { 
      title: 'Heritage Carrots', 
      origin: 'Friesland, NL', 
      price: '€2.20 / kg', 
      image: 'https://images.unsplash.com/photo-1582515073490-39981397c445?auto=format&fit=crop&q=80&w=800' 
    },
    { 
      title: 'Blue Fin Tuna', 
      origin: 'Noordzee', 
      price: '€65.00 / kg', 
      image: tunaImage 
    },
  ];

  const suppliers = [
    { name: 'Van der Meer Farm', location: 'Utrecht, NL', type: 'Biologische Producten', rating: '4.9', productCount: 124, image: 'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&q=80&w=400' },
    { name: 'De Ambachtelijke Fromagerie', location: 'Lyon, FR', type: 'Zuivel & Kaas', rating: '4.8', productCount: 86, image: 'https://images.unsplash.com/photo-1559181567-c3190ca9959b?auto=format&fit=crop&q=80&w=400' },
    { name: 'Noordzee Catch', location: 'Zeebrugge, BE', type: 'Duurzame Vis', rating: '5.0', productCount: 42, image: 'https://images.unsplash.com/photo-1534080564583-6be75777b70a?auto=format&fit=crop&q=80&w=400' },
    { name: 'Highland Provenance', location: 'Inverness, UK', type: 'Premium Vlees', rating: '4.7', productCount: 65, image: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&q=80&w=400' },
  ];

  const logos = ['Noma', 'Geranium', 'Hof van Cleve', 'The Jane'];

  return (
    <div className="flex flex-col min-h-screen bg-[#FCFAF8]">
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&q=80&w=2000" alt="Kitchen background" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl" >
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-xs font-bold uppercase tracking-[0.2em] mb-8">
              {t('home.heroTag')}
            </span>
            <h1 className="text-6xl md:text-8xl font-bold text-white mb-8 leading-[1.05] tracking-tight">
              {t('home.heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-xl leading-relaxed font-light">
              {t('home.heroSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <Link to="/marketplace">
                <Button size="lg" className="bg-white text-black hover:bg-zinc-100 min-w-[200px] h-14 text-base rounded-full">
                  {t('home.primaryCta')}
                </Button>
              </Link>
              <Link to="/login" className="w-full sm:w-auto">
                <Button variant="ghost" size="lg" className="text-white hover:bg-white/10 min-w-[200px] h-14 text-base rounded-full border border-white/30">
                  {t('home.secondaryCta')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 w-full bg-black/20 backdrop-blur-xl border-t border-white/10 py-8">
          <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-center gap-4 text-white">
              <SafeIcon icon={FiAward} className="w-6 h-6 text-[#D4B483]" />
              <div>
                <p className="text-lg font-bold">2,500+</p>
                <p className="text-xs text-white/60 uppercase tracking-wider">{t('home.stats.suppliers')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-white">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-[#D4B483]" />
              <div>
                <p className="text-lg font-bold">45,000+</p>
                <p className="text-xs text-white/60 uppercase tracking-wider">{t('home.stats.products')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-white">
              <SafeIcon icon={FiTruck} className="w-6 h-6 text-[#D4B483]" />
              <div>
                <p className="text-lg font-bold">Gestreefd wordt naar 24u</p>
                <p className="text-xs text-white/60 uppercase tracking-wider">{t('home.stats.delivery')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-48 px-6">
        <div className="container mx-auto">
          <div className="flex items-end justify-between mb-24">
            <div className="max-w-2xl">
              <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-zinc-900 mb-8">{t('home.products.title')}</h2>
              <p className="text-xl text-zinc-500 font-light">{t('home.products.subtitle')}</p>
            </div>
            <Link to="/marketplace">
              <Button variant="secondary" className="hidden md:flex rounded-full px-8 gap-2">
                {t('home.products.viewAll')} <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((prod, idx) => (
              <ProductCard key={idx} {...prod} delay={idx * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust Logos */}
      <section className="py-24 border-b border-zinc-100 bg-white">
        <div className="container mx-auto px-6 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-400 mb-10">{t('home.logos.title')}</p>
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-24 opacity-40 grayscale">
            {logos.map(logo => (
              <span key={logo} className="text-2xl font-editorial italic text-zinc-900">{logo}</span>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-48 bg-[#FCFAF8] border-y border-zinc-100">
        <div className="container mx-auto px-6">
          <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-center text-zinc-900 mb-24">{t('home.howItWorks.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 lg:gap-32">
            <Step number="1" icon={FiSearch} title={t('home.howItWorks.step1Title')} description={t('home.howItWorks.step1Desc')} delay={0.1} />
            <Step number="2" icon={FiShoppingBag} title={t('home.howItWorks.step2Title')} description={t('home.howItWorks.step2Desc')} delay={0.2} />
            <Step number="3" icon={FiLayers} title={t('home.howItWorks.step3Title')} description={t('home.howItWorks.step3Desc')} delay={0.3} />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-48 px-6">
        <div className="container mx-auto">
          <div className="max-w-2xl mb-24">
            <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-zinc-900 mb-8">{t('home.categories.title')}</h2>
            <p className="text-xl text-zinc-500 font-light">{t('home.categories.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((cat, idx) => (
              <CategoryCard key={idx} {...cat} delay={idx * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-48 bg-[#1A2E22] text-white overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} >
              <h2 className="text-6xl md:text-7xl font-bold tracking-tight mb-10 leading-tight">
                {t('home.story.title')}
              </h2>
              <p className="text-xl md:text-2xl font-light text-white/80 leading-relaxed mb-12">
                {t('home.story.description')}
              </p>
              <Button variant="ghost" className="text-white border-white/30 hover:bg-white/10 rounded-full h-14 px-10">
                {t('home.story.cta')}
              </Button>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="relative aspect-square" >
              <img src="https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?auto=format&fit=crop&q=80&w=1000" alt="Chef gathering ingredients" className="w-full h-full object-cover rounded-3xl" />
              <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#D4B483] rounded-3xl z-[-1]" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Suppliers */}
      <section className="py-48 px-6 bg-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-24">
            <div className="max-w-2xl">
              <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-zinc-900 mb-8">{t('home.suppliers.title')}</h2>
              <p className="text-xl text-zinc-500 font-light">{t('home.suppliers.subtitle')}</p>
            </div>
            <Button variant="secondary" className="rounded-full h-12">
              {t('home.suppliers.viewAll')}
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
            {suppliers.map((sup, idx) => (
              <SupplierCard key={idx} {...sup} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-48 bg-[#1A2E22]/5">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="p-12 bg-white rounded-[3rem] shadow-sm border border-zinc-100 flex flex-col gap-8">
              <SafeIcon icon={FiQuote} className="w-12 h-12 text-[#D4B483]" />
              <p className="text-2xl font-editorial italic text-zinc-900 leading-relaxed">
                "{t('home.testimonials.quote1')}"
              </p>
              <div>
                <p className="font-bold text-zinc-900">{t('home.testimonials.author1')}</p>
                <p className="text-sm text-zinc-500">{t('home.testimonials.role1')}</p>
              </div>
            </div>
            <div className="p-12 bg-[#1A2E22] text-white rounded-[3rem] shadow-xl flex flex-col gap-8">
              <SafeIcon icon={FiQuote} className="w-12 h-12 text-[#D4B483]" />
              <p className="text-2xl font-editorial italic text-white/90 leading-relaxed">
                "{t('home.testimonials.quote2')}"
              </p>
              <div>
                <p className="font-bold text-white">{t('home.testimonials.author2')}</p>
                <p className="text-sm text-white/60">{t('home.testimonials.role2')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Triggers */}
      <section className="py-32 bg-white border-t border-zinc-100">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <SafeIcon icon={FiCheck} className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-zinc-900">{t('home.trust.verified')}</h4>
              <p className="text-sm text-zinc-500">{t('home.trust.verifiedDesc')}</p>
            </div>
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
                <SafeIcon icon={FiTruck} className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-zinc-900">{t('home.trust.delivery')}</h4>
              <p className="text-sm text-zinc-500">{t('home.trust.deliveryDesc')}</p>
            </div>
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <SafeIcon icon={FiShield} className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-zinc-900">{t('home.trust.secure')}</h4>
              <p className="text-sm text-zinc-500">{t('home.trust.secureDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-48 px-6 bg-[#FCFAF8]">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-zinc-900 rounded-[3rem] p-12 md:p-24 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
              <img src="https://images.unsplash.com/photo-1550989460-0adf9ea622e2?auto=format&fit=crop&q=80&w=1000" className="w-full h-full object-cover" alt="" />
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-8 relative z-10">{t('home.cta.title')}</h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 relative z-10">
              <Link to="/login">
                <Button size="lg" className="bg-[#D4B483] text-[#1A2E22] hover:bg-[#c4a473] h-14 px-12 rounded-full font-bold">
                  {t('home.cta.primary')}
                </Button>
              </Link>
              <Button variant="ghost" className="text-white border-white/20 hover:bg-white/10 h-14 px-12 rounded-full">
                {t('home.cta.secondary')}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};